import { FormFieldType } from "~/components/form/fields";
import type { FormStructure } from "./form-util";

export function flattenObject(obj: any, prefix = ""): Record<string, any> {
  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.assign(acc, flattenObject(value, newKey));
      } else {
        acc[newKey] = value;
      }

      return acc;
    },
    {} as Record<string, any>,
  );
}

export function exportToCsv(filename: string, rows: any[]) {
  if (!rows.length) return;

  // Flatten all rows first
  const flattened = rows.map((r) => flattenObject(r));

  // Collect all unique keys (columns)
  const headers = Array.from(
    new Set(flattened.flatMap((row) => Object.keys(row))),
  );

  const csv = [
    headers.join(","), // header row
    ...flattened.map((row) =>
      headers.map((h) => JSON.stringify(row[h] ?? "")).join(","),
    ),
  ].join("\n");

  // Trigger download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Escape values for CSV (handle commas, quotes, newlines)
function escapeCsvValue(value: any): string {
  if (value == null) return "";
  const str = String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportFormCsv<T>(rows: T[], structure: FormStructure<T>) {
  const headers = Object.keys(structure);

  const csvRows = [
    headers
      .map((key) => key)
      .filter((d) => d !== "virtualFields")
      .join(","), // header row
    ...rows.map((row) =>
      headers
        .filter((d) => d !== "virtualFields")
        .map((key) => escapeCsvValue((row as any)[key]))
        .join(","),
    ),
  ];

  const csv = csvRows.join("\n");

  // trigger download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "data.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function parseValue(value: string): any {
  // Add custom parsing logic based on key or expected type if needed
  // For now, just return the string or convert to number/boolean if applicable
  if (value === "") return "";

  if (!isNaN(Number(value))) return Number(value);
  if (value.toLowerCase() === "true") return true;
  if (value.toLowerCase() === "false") return false;

  return value;
}

function getDefaultImportValueByStructure<T>(
  key: string,
  structure: FormStructure<T>,
) {
  if (key === "virtualFields") return undefined;
  const def = structure[key as keyof T];
  switch (def.type) {
    case FormFieldType.CHECKBOX:
      return false;
    default:
      return undefined;
  }
}

export function importFormCsv<T>(
  file: File,
  structure: FormStructure<T>,
  onImport: (rows: T[]) => void,
) {
  const reader = new FileReader();
  reader.onload = (event) => {
    const text = event.target?.result as string;
    if (!text) return;

    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) {
      console.warn("Not enough rows in CSV File");
      return;
    }

    const headers = lines[0].split(",").map((h) => h.trim());
    const keys = Object.keys(structure);

    const rows: (T | undefined)[] = lines.slice(1).map((line) => {
      const values = line.split(",").map((v) => v.trim());
      if (values.length !== headers.length) {
        console.warn("Row length does not match header length:", line);
        return;
      }

      const mapped: any = {};
      keys.forEach((key) => {
        const idx = headers.indexOf(key);
        if (key === "virtualFields") return; // skip virtual fields during import

        if (idx <= 0) {
          mapped[key] = getDefaultImportValueByStructure(key, structure);
          return;
        }

        let parsedValue = parseValue(values[idx]?.replace(/^"|"$/g, "") ?? "");
        if (parsedValue == "")
          parsedValue = getDefaultImportValueByStructure(key, structure);

        mapped[key] =
          idx >= 0
            ? parsedValue
            : getDefaultImportValueByStructure(key, structure);
      });

      return mapped as T;
    });

    onImport(rows.filter((r): r is T => r !== undefined));
  };
  reader.readAsText(file);
}
